import { Component, Host, h, State, Prop } from "@stencil/core";
import {
  ILiftStatusSensorDataResponse,
  TSeverity,
  ILiftStatusWebComponentError,
} from "../../utils/types";
import { getStore, ILiftStatusStore } from "../../store";
import statusCodesState from "../../stores/status-codes.store";
import { isEmptyObject } from "../../utils/is-empty-object";
import { isNil } from "lodash-es";
import {
  applyErrorMessage,
  clearErrorMessage,
  getCompactErrorMessage,
} from "../../utils/error-message";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";

@Component({
  tag: "ls-status-count",
  styleUrl: "ls-status-count.css",
  shadow: true,
})
export class LsStatusCount {
  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * The impact level to filter messages by.
   * Can be "impact", "non-impact", or "informational".
   */
  @Prop() readonly impactLevel: TSeverity;

  /**
   * The count of unique status messages for this impact level.
   */
  @State() messageCount = 0;

  /**
   * Whether the component is still loading data.
   */
  @State() isLoading = true;

  /**
   * Centralized error state for tracking component errors.
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: "",
  };

  private store: ILiftStatusStore | null = null;

  /**
   * The sensor ID associated with the status messages.
   */
  private readonly sensorId = 513;

  private isComponentMounted = false;
  private storeUnsubscribe: (() => void) | null = null;
  private containerElement: HTMLElement;

  /**
   * Validates that the impact level prop is valid.
   */
  private isValidImpactLevel(value: string): value is TSeverity {
    return ["impact", "informational", "non-impact"].includes(value);
  }

  /**
   * Updates the error state based on current component conditions.
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      if (this.containerElement !== undefined) {
        applyErrorMessage(
          this.containerElement,
          getCompactErrorMessage(
            this.errorState.messageKey,
            this.store?.state.locale,
          ),
        );
      }
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      if (this.containerElement !== undefined) {
        applyErrorMessage(
          this.containerElement,
          getCompactErrorMessage(
            this.errorState.messageKey,
            this.store?.state.locale,
          ),
        );
      }
      return;
    }

    // No error detected
    this.errorState = {
      hasError: false,
      messageKey: undefined,
      type: "",
    };

    if (this.containerElement !== undefined) {
      clearErrorMessage(this.containerElement);
    }
  }

  /**
   * Processes the raw data from the socket server and counts unique messages.
   */
  private updateData = (sensorData: ILiftStatusSensorDataResponse) => {
    if (!this.isComponentMounted) return;

    this.updateErrorState();

    // Only listen to our own sensor identifier
    if (isEmptyObject(sensorData?.values?.[this.sensorId])) {
      this.isLoading = false;
      return;
    }

    const items = sensorData.values[this.sensorId]
      ?.split(",")
      .filter((id) => id.trim() !== "");

    if (isNil(items) || items.length === 0) {
      this.messageCount = 0;
      this.isLoading = false;
      return;
    }

    // Use a Set to store unique status code identifiers for this impact level
    const uniqueIdentifiers = new Set<string>();

    for (const item of items) {
      const parts = item.split(":");
      const identifier = parts[0];
      const active = parseInt(parts[2], 10);

      // Get the severity level from the central store
      const severity = statusCodesState[identifier]?.level ?? null;

      // Only count active messages that match our impact level
      if (active === 1 && severity === this.impactLevel) {
        uniqueIdentifiers.add(identifier);
      }
    }

    this.messageCount = uniqueIdentifiers.size;
    this.isLoading = false;
  };

  async componentWillLoad() {
    // Validate impactLevel prop
    if (!this.isValidImpactLevel(this.impactLevel)) {
      console.group(
        "%cError%c [LsStatusCount] Invalid impact level supplied",
        "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
        "",
      );
      console.log("Allowed options: 'impact', 'non-impact', 'informational'");
      console.log(`Received: '${this.impactLevel}'`);
      console.groupEnd();
      this.isLoading = false;
      return;
    }

    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, () => {
      this.updateErrorState();
    });

    this.isComponentMounted = true;
    this.updateData(this.store.state.currentSensorData);

    const sensorDataUnsubscribe = this.store.onChange(
      "currentSensorData",
      (sensorData) => {
        this.updateData(sensorData);
      },
    );

    const errorUnsubscribe = this.store.onChange("error", () => {
      this.updateErrorState();
    });

    this.storeUnsubscribe = () => {
      sensorDataUnsubscribe();
      errorUnsubscribe();
    };
  }

  componentDidLoad() {
    // Ensure overlay-based error UI can be applied once the container ref exists.
    this.updateErrorState();
  }

  disconnectedCallback() {
    this.isComponentMounted = false;

    // Unsubscribe from store changes
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }

    if (this.containerElement !== undefined) {
      clearErrorMessage(this.containerElement);
    }
  }

  /**
   * Returns the label for the impact level.
   */
  private getImpactLabel(): string {
    switch (this.impactLevel) {
      case "impact":
        return this.store.t("ui:impact_level.impact");
      case "non-impact":
        return this.store.t("ui:impact_level.non_impact");
      case "informational":
        return this.store.t("ui:impact_level.informational");
      default:
        return this.store.t("ui:impact_level.unknown");
    }
  }

  /**
   * Renders the impact icon (octagon with exclamation mark).
   */
  private renderImpactIcon() {
    return (
      <svg
        class="icon impact-icon"
        part="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 16h.01M12 8v4m3.312-10a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z"
        />
      </svg>
    );
  }

  /**
   * Renders the non-impact icon (triangle warning).
   */
  private renderNonImpactIcon() {
    return (
      <svg
        class="icon non-impact-icon"
        part="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m21.73 18l-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3M12 9v4m0 4h.01"
        />
      </svg>
    );
  }

  /**
   * Renders the informational icon (circle with info mark).
   */
  private renderInformationalIcon() {
    return (
      <svg
        class="icon informational-icon"
        part="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <g
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4m0-4h.01" />
        </g>
      </svg>
    );
  }

  /**
   * Renders the appropriate icon based on the impact level.
   */
  private renderIcon() {
    switch (this.impactLevel) {
      case "impact":
        return this.renderImpactIcon();
      case "non-impact":
        return this.renderNonImpactIcon();
      case "informational":
        return this.renderInformationalIcon();
      default:
        return null;
    }
  }

  render() {
    if (!this.isValidImpactLevel(this.impactLevel)) {
      return (
        <Host>
          <div class="ls-status-count error">
            {this.store?.t("ui:impact_level.invalid") ?? "Ongeldig impactniveau"}
          </div>
        </Host>
      );
    }

    const classes = [
      "ls-status-count",
      this.impactLevel,
      this.isLoading && "loading",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Host>
        <div
          class={classes}
          part="container"
          ref={(el) => {
            this.containerElement = el;
          }}
        >
          <div class="header" part="header">
            {this.renderIcon()}
            <span class="label" part="label">
              {this.getImpactLabel()}
            </span>
          </div>
          <span class="count" part="count">
            {this.messageCount}
          </span>
        </div>
      </Host>
    );
  }
}
